import { useContext, useEffect, useState } from 'react';

import {
	ArrowLeftOutlined,
	DeleteOutlined,
	EditOutlined,
	SearchOutlined,
} from '@ant-design/icons';
import {
	Button,
	Flex,
	Input,
	Popconfirm,
	Tooltip,
	Typography,
	message,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

import {
	useDeleteSingleCollection,
	useGetSingleVideosCollectionById,
} from '@/api/monitor_collections';
import EditCollectionModal from '@/components/modals/EditCollectionModal';
import { useDebounce } from '@/hooks';
import { CollectionContext } from '@/providers/CollectionsContextProvider';
import { IPagination } from '@/ts';

import SingleCollectionVideosTable from './SingleCollectionVideosTable';
import { singleCollectionColumns } from './data';

export default function VideosGroupCollection() {
	const params = useParams();
	const navigate = useNavigate();
	const { collection, setCollection, modalData, setModalData, data } =
		useContext(CollectionContext);
	const [isEditCollectionModalOpen, setIsEditCollectionModalOpen] =
		useState(false);
	// const [sortBy, setSortBy] = useState<"created_at" | "status">("created_at");
	const [search, setSearch] = useState('');
	const [sort, setSort] = useState<'requested_at' | 'status'>('requested_at');
	const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

	const [pagination, setPagination] = useState<IPagination>({
		page: Number(localStorage.getItem('page') ?? 1),
		pageCount: 1,
		total: 0,
		limit: Number(localStorage.getItem('limit') ?? 10),
	});

	const debouncedSearch = useDebounce(search);

	const {
		data: collectionData,
		isError: isCollectionDataError,
		error: collectionDataError,
		// isSuccess: usersSuccess,
		refetch,
		isLoading,
		isFetching,
	} = useGetSingleVideosCollectionById({
		id: params?.id,
		pagination,
		sort,
		sortOrder,
		enabled: collection === 'videos-group' && !!params?.id,
		search: debouncedSearch.length >= 3 ? debouncedSearch : '',
	});

	useEffect(() => {
		if (
			isCollectionDataError &&
			(collectionDataError as any)?.response?.status === 404
		) {
			navigate(
				`/app/dashboard/video-insights-co-pilot/collections/videos/${params?.id}/_`,
			);
		} else if (isCollectionDataError && collectionDataError) {
			message.error('An error occurred while fetching the collection data.');
		}
	}, [isCollectionDataError, collectionDataError]);

	const { mutate: deleteCollection, isLoading: isDeleteLoading } =
		useDeleteSingleCollection('videos');

	const item = collectionData?.data?.data?.item || {};
	const items = collectionData?.data?.data?.items || [];
	const total = collectionData?.data?.data?.pagination?.total || 0;

	const onTableChange = (__: any, _: any, sorter: any) => {
		setSort(sorter.field ?? 'requested_at');

		setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
	};

	const onDeleteClick = (id: number) => {
		deleteCollection(
			{
				id,
				type: 'videos',
			},
			{
				onSuccess: () => {
					message.success('Removed');
					setCollection('videos');
					setSearch('');
				},
				onError: (error) => {
					const err = error as any;
					console.error(
						'Error deleting collection:',
						err.response.data.message,
					);
					if (err?.response?.data?.message) {
						message.error(err.response.data.message);
					} else {
						message.error('An error occurred while deleting the collection.');
					}
				},
			},
		);
	};

	if (collection !== 'videos-group') {
		return null;
	}

	return (
		<>
			<Flex vertical gap={8} align="flex-start" style={{ width: '100%' }}>
				<Flex
					gap={8}
					align="center"
					justify="space-between"
					style={{ width: '100%' }}
				>
					{(item?.title ?? data?.title) && (
						<Flex vertical>
							<Typography.Title
								level={5}
								style={{ textAlign: 'left', margin: '0' }}
							>
								{item?.title ?? data?.title}
							</Typography.Title>

							{(item?.description ?? data?.description) && (
								<Typography.Text style={{ color: 'gray' }}>
									{item?.description ?? data?.description}
								</Typography.Text>
							)}
						</Flex>
					)}
					<Flex gap={8} style={{ marginLeft: 'auto' }}>
						<Tooltip title="Edit">
							<Button
								onClick={() => {
									setIsEditCollectionModalOpen(true);
									setModalData({
										id: Number(params?.id),
										title: item?.title,
										description: item?.description,
									});
								}}
								disabled={isDeleteLoading || isFetching || !item?.title}
							>
								<EditOutlined />
							</Button>
						</Tooltip>
						<Popconfirm
							title={`Sure to remove collection?`}
							onConfirm={() => onDeleteClick(Number(params?.id))}
						>
							<Tooltip title="Remove">
								<Button
									style={{ fontSize: '16px' }}
									loading={isDeleteLoading}
									disabled={isDeleteLoading || isFetching || !item?.title}
								>
									<DeleteOutlined />
								</Button>
							</Tooltip>
						</Popconfirm>

						<Tooltip title="Back">
							<Button
								onClick={() => {
									setCollection('videos');
									navigate(
										'/app/dashboard/video-insights-co-pilot/collections/videos',
									);
									setSearch('');
								}}
							>
								<ArrowLeftOutlined />
							</Button>
						</Tooltip>
					</Flex>
				</Flex>
				<Flex style={{ width: '100%' }} justify="space-between" align="center">
					<Input
						placeholder="Search..."
						suffix={<SearchOutlined />}
						// style={{ width: "200px", maxWidth: "200px" }}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</Flex>
				<SingleCollectionVideosTable
					data={items}
					columns={singleCollectionColumns}
					onTableChange={onTableChange}
					isLoading={isLoading}
					isFetching={isFetching}
					pagination={{
						...pagination,
						total,
					}}
					setPagination={setPagination}
				/>
			</Flex>
			<EditCollectionModal
				isOpen={isEditCollectionModalOpen}
				setOpen={setIsEditCollectionModalOpen}
				data={{
					...modalData,
					type: 'videos',
				}}
				setData={setModalData}
			/>
		</>
	);
}
